import { createSlice } from "@reduxjs/toolkit";
import { contractApi } from "../../api/contractApi";
import { toast } from "../../components/common/toast";
import type { AppDispatch, RootState } from "../../app/store";
import type {
  Contract,
  UpdateContractPayload,
} from "../../types/contract.types";
import { getCurrentUser } from "../../auth/auth.service";
import { getApiErrorMessage } from "../../utils/apiError";
import { contractWorkflowApi } from "../../api/contractWorkflowApi";
import { ContractWorkflowStatus } from "../../utils/contractStatus";
import i18n from "../../i18n";
import {
  applyDelete,
  applyRename,
  buildDeletePayload,
  buildRenamePayload,
  createResolvedMedia,
  uploadMediaFiles,
} from "../../utils/mediaUtils";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

interface ContractsSearchPayload {
  /** Pickers pass true so deactivated records are never offered as a choice. */
  isActive?: boolean;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  sortDecending?: boolean;
  includeFacets?: boolean;
  organisationIds?: string[];
  groupIds?: string[];
  ids?: string[];
  title?: string[];
  statuses?: string[];
  myMode?: boolean;
  searchText?: string;
  startDate?: string;
  endDate?: string;
  contractNames?: string[];
  contractCategorys?: string[];
  contractTitles?: string[];
  /** Exact-match lookup — finds a contract by number even off the current page. */
  contractNumber?: string;
  /** Which listing this is for. `false` gives the normal Contracts list,
   *  `true` the Legacy Contracts list. Omit only where both belong together
   *  (dashboard totals, connected-contract lookups). */
  isLegacy?: boolean;
  /** Matches contracts where any of these member ids is the initiator, editor, a reviewer, or an approver. */
  assignedMemberIds?: string[];
  /** Filters on the contract's own end/expiry date — distinct from startDate/endDate (creation date) above. */
  contractEndDateFrom?: string;
  contractEndDateTo?: string;
  includeDeleted?: boolean;
  /** Whether each row should carry its document body (`templateJson`).
   *
   *  Defaults to true so the detail page, the edit form and quick view keep
   *  working unchanged. The listings pass false: they never render the
   *  document, and it is by far the largest thing on a contract — a 10-row
   *  page measured 2,750 KB, of which 2,658 KB (97%) was templateJson,
   *  taking ~2.7s against ~250ms for the same query without it. */
  includeDocument?: boolean;
}

// The newest first-page request. A fresh search term must not be dropped
// because an earlier one is still loading, so a first page always runs and an
// answer to anything older than it is discarded.
let latestFirstPage = 0;

export const fetchContracts = (payload: ContractsSearchPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().contracts;
    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) return;
    const ticket = isFirstPage ? ++latestFirstPage : latestFirstPage;
    const superseded = () => ticket !== latestFirstPage;

    // Set before the first await (getCurrentUser) rather than after, so a
    // second call fired while this one is still resolving that awaited
    // call sees isLoading:true and bails out at the guard above instead of
    // both calls racing past it and appending the same page twice — the
    // observed bug: the intersection observer that triggers the next page
    // (see ContractsListPage's lastElementRef) can re-fire immediately
    // when it's recreated on a re-render while its target is still
    // visible, and previously both firings passed the guard because
    // neither had dispatched setContractLoading(true) yet.
    dispatch(setContractLoading(true));

    try {
      const userData = await getCurrentUser();

      const formattedStatuses = (payload.statuses || [])
        .map((status) => Number(status))
        .filter((status) => !isNaN(status));

      const requestBody = {
        pageSize: 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "",
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
        sortAscending: payload.sortAscending ?? false,
        // Off by default: nothing in the app reads facets, and computing
        // them slowed every list request.
        includeFacets: payload.includeFacets ?? false,
        // Always scoped to the current (switched) organisation — never let a
        // caller-supplied filter override the tenant boundary.
        groupIds: [`${userData.companyId}`],
        ids: payload.ids || [],
        currentProfileId: userData.currentLoggedInUserId || "",
        title: payload.title || [],
        contractStatuses: formattedStatuses,
        myMode: payload.myMode ?? true,
        searchText: payload.searchText || "",
        ...(payload.startDate && { startDate: payload.startDate }),
        ...(payload.endDate && { endDate: payload.endDate }),
        ...(payload.contractNumber && {
          contractNumber: payload.contractNumber,
        }),
        ...(payload.assignedMemberIds?.length && {
          assignedMemberIds: payload.assignedMemberIds,
        }),
        ...(payload.contractEndDateFrom && {
          contractEndDateFrom: payload.contractEndDateFrom,
        }),
        ...(payload.contractEndDateTo && {
          contractEndDateTo: payload.contractEndDateTo,
        }),
        // A lookup by id (a detail page, often opened fresh from the URL)
        // must find the record whatever its state: without this a deleted
        // record's page loaded once from the list and then came back empty
        // on reload, because the search defaults to excluding deleted rows.
        includeDeleted: payload.includeDeleted ?? !!payload.ids?.length,
        includeDocument: payload.includeDocument !== false,
        // Sent only when the caller cares. Legacy and normal contracts live in
        // one store, so without this every legacy contract would surface in
        // the main Contracts list; omitting it deliberately means "both",
        // which is what dashboard counts and lookups want.
        ...(payload.isLegacy !== undefined && { isLegacy: payload.isLegacy }),
      };

      const response = await contractApi.searchContracts(requestBody);
      if (superseded()) return;

      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalContracts(totalCount));

      // Media links are resolved for the whole page in one request per
      // (organisation, media type), not per contract.
      //
      // This used to run inside the per-contract map: one call for the logo
      // and another for the attachments, so a 10-row page opened up to 20
      // requests and they were the slowest thing left on the listing (8
      // calls, 1.85s measured). getMediaLink already accepts an array of
      // ids, and every contract on a page shares one groupId in practice,
      // so the same work collapses into two calls.
      const LOGO_MEDIA_TYPE = 1;
      const ATTACHMENT_MEDIA_TYPE = 3;

      /** groupId -> media type -> ids to resolve. */
      const wanted = new Map<string, Map<number, Set<string>>>();
      const want = (
        groupId: string | undefined,
        type: number,
        id?: string | null,
      ) => {
        if (!id || !groupId) return;
        if (!wanted.has(groupId)) wanted.set(groupId, new Map());
        const byType = wanted.get(groupId)!;
        if (!byType.has(type)) byType.set(type, new Set());
        byType.get(type)!.add(id);
      };

      // A logo that is already a URL needs no lookup at all.
      const isDirectUrl = (v?: string | null) =>
        !!v && (v.startsWith("http") || v.startsWith("blob:"));

      for (const contract of fetchedData as Contract[]) {
        // Not displayNameDomainLogoUrl: that field carries the subdomain.
        const targetPic = contract.profilePic || contract.logo;
        if (targetPic && !isDirectUrl(targetPic)) {
          want(contract.groupId, LOGO_MEDIA_TYPE, targetPic);
        }
        for (const media of contract.medias || []) {
          want(contract.groupId, ATTACHMENT_MEDIA_TYPE, media.s3Url);
        }
      }

      /** "type:id" -> signed link. */
      const linkByKey = new Map<string, string>();
      await Promise.all(
        [...wanted.entries()].flatMap(([groupId, byType]) =>
          [...byType.entries()].map(async ([type, ids]) => {
            try {
              const res = await contractApi.getMediaLink(
                [...ids],
                type,
                groupId,
              );
              for (const link of res.links || []) {
                if (link?.id) linkByKey.set(`${type}:${link.id}`, link.link);
              }
            } catch (err) {
              // Same outcome as the previous per-contract catch: the rows in
              // this group render without their media rather than the whole
              // listing failing.
              console.error("Contract media resolution failed:", err);
            }
          }),
        ),
      );

      if (superseded()) return;

      const resolvedData = (fetchedData as Contract[]).map(
        (contract: Contract) => {
          const updatedContract = {
            ...contract,
            contractParties:
              contract.contractPartiesRes || contract.contractParties || [],
          } as Contract;

          const targetPic = contract.profilePic || contract.logo;

          if (targetPic) {
            updatedContract.profilePicUrl = isDirectUrl(targetPic)
              ? targetPic
              : linkByKey.get(`${LOGO_MEDIA_TYPE}:${targetPic}`) || "";
          }

          if (contract.medias && contract.medias.length > 0) {
            updatedContract.medias = contract.medias.map((media) => ({
              ...media,
              url:
                linkByKey.get(`${ATTACHMENT_MEDIA_TYPE}:${media.s3Url}`) || "",
            }));
            updatedContract.mediaLinks = null;
          }

          return updatedContract;
        },
      );

      const currentLoadedCount =
        incomingPageIndex === 0
          ? resolvedData.length
          : state.items.length + resolvedData.length;

      if (currentLoadedCount >= totalCount || resolvedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setContracts(resolvedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendContracts(resolvedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Contracts error:", error);
      toast.error(
        i18n.t("common:slices.contracts.fetchError", {
          defaultValue: "Failed to fetch contracts",
        }),
      );
    } finally {
      if (!superseded()) dispatch(setContractLoading(false));
    }
  };
};

// The category / type / title lookups fill filter dropdowns. They keep their
// own in-flight flags: they used to set (and wait on) the list's isLoading,
// so one started during a list search was dropped, and one finishing early
// told the list's infinite scroll the search was over.
const lookupInFlight = { type: false, category: false, title: false };

export const fetchContractsByType = (payload: ContractsSearchPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().contracts;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    if (lookupInFlight.type || (!state.hasMoreByType && incomingPageIndex !== 0))
      return;

    try {
      lookupInFlight.type = true;

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "",
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
        sortDecending: payload.sortDecending ?? true,
        // Always scoped to the current (switched) organisation.
        groupIds: [`${userData.companyId}`],
        ids: payload.ids || [],
        contractNames: payload.contractNames || [],
      };

      const response = await contractApi.searchContractsType(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalContractsByType(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.contractTypes.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMoreByType(false));
      } else {
        dispatch(setHasMoreByType(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setContractTypes(fetchedData));
        dispatch(setPageIndexByType(1));
      } else {
        dispatch(appendContractTypes(fetchedData));
        dispatch(setPageIndexByType(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Contracts By Type error:", error);
      toast.error(
        i18n.t("common:slices.contracts.fetchError", {
          defaultValue: "Failed to fetch contracts",
        }),
      );
    } finally {
      lookupInFlight.type = false;
    }
  };
};

export const fetchContractsByCategory = (
  payload: ContractsSearchPayload = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().contracts;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndexByCategory;

    if (!state.hasMoreByCategory && incomingPageIndex !== 0) return;

    try {
      lookupInFlight.category = true;

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "",
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
        sortDecending: payload.sortDecending ?? true,
        // Always scoped to the current (switched) organisation.
        groupIds: [`${userData.companyId}`],
        ids: payload.ids || [],
        contractCategorys: payload.contractCategorys || [],
      };

      const response = await contractApi.searchContractsCateg(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalContractCategories(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.contractCategories.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMoreByCategory(false));
      } else {
        dispatch(setHasMoreByCategory(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setContractCategories(fetchedData));
        dispatch(setPageIndexByCategory(1));
      } else {
        dispatch(appendContractCategories(fetchedData));
        dispatch(setPageIndexByCategory(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Contracts By Category error:", error);
      toast.error(
        i18n.t("common:slices.contracts.fetchCategoriesError", {
          defaultValue: "Failed to fetch contract categories",
        }),
      );
    } finally {
      lookupInFlight.category = false;
    }
  };
};

export const addContractCategory = (contractCategory: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const requestBody = {
        contractCategory: contractCategory,
      };
      const res = await contractApi.createContractsCateg(requestBody);
      const newCategoryItem = {
        id: res,
        contractCategory: contractCategory,
      };

      const { contractCategories } = getState().contracts;
      dispatch(setContractCategories([newCategoryItem, ...contractCategories]));

      toast.success(
        i18n.t("common:slices.contracts.categoryAddSuccess", {
          defaultValue: "Contract category created successfully!",
        }),
      );
      return newCategoryItem;
    } catch (err) {
      console.error("Create Contract Category Error:", err);
      // Rethrown, not swallowed. Returning undefined here left the caller
      // (FormStep1's add-modal) setting the typed name on the form with a
      // null id beside it: the field looked filled and passed validation —
      // which only checks the name — and the whole contract submission then
      // failed at the very end on an id the server had never seen.
      toast.error(
        i18n.t("common:slices.contracts.categoryAddError", {
          defaultValue: "Failed to create contract category",
        }),
      );
      throw err;
    }
  };
};

export const fetchContractsByTitle = (payload: ContractsSearchPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().contracts;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndexByTitle;

    if (lookupInFlight.title || (!state.hasMoreByTitle && incomingPageIndex !== 0))
      return;

    try {
      lookupInFlight.title = true;

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "",
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
        sortDecending: payload.sortDecending ?? true,
        // Always scoped to the current (switched) organisation.
        groupIds: [`${userData.companyId}`],
        ids: payload.ids || [],
        contractTitles: payload.contractTitles || [],
      };

      const response = await contractApi.searchContractsTitle(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalContractTitles(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.contractTitles.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMoreByTitle(false));
      } else {
        dispatch(setHasMoreByTitle(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setContractTitles(fetchedData));
        dispatch(setPageIndexByTitle(1));
      } else {
        dispatch(appendContractTitles(fetchedData));
        dispatch(setPageIndexByTitle(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Contracts By Title error:", error);
      toast.error(
        i18n.t("common:slices.contracts.fetchTitlesError", {
          defaultValue: "Failed to fetch contract titles",
        }),
      );
    } finally {
      lookupInFlight.title = false;
    }
  };
};

export const addContractTitle = (contractTitle: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const requestBody = {
        contractTitle: contractTitle,
      };
      const res = await contractApi.createContractsTitle(requestBody);
      const newTitleItem = {
        id: res,
        contractTitle: contractTitle,
      };

      const { contractTitles } = getState().contracts;
      dispatch(setContractTitles([newTitleItem, ...contractTitles]));

      toast.success(
        i18n.t("common:slices.contracts.titleAddSuccess", {
          defaultValue: "Contract title created successfully!",
        }),
      );
      return newTitleItem;
    } catch (err) {
      console.error("Create Contract Title Error:", err);
      // Rethrown, not swallowed. Returning undefined here left the caller
      // (FormStep1's add-modal) setting the typed name on the form with a
      // null id beside it: the field looked filled and passed validation —
      // which only checks the name — and the whole contract submission then
      // failed at the very end on an id the server had never seen.
      toast.error(
        i18n.t("common:slices.contracts.titleAddError", {
          defaultValue: "Failed to create contract title",
        }),
      );
      throw err;
    }
  };
};

export const uploadContractMedias = (contractId: string, files: File[]) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const state = getState();
      const userId = (await getCurrentUser()).userId;
      const groupId = state.contracts.items.find(
        (item: Contract) => item.id === contractId,
      )?.groupId;

      const { payloads, links } = await uploadMediaFiles(
        contractApi,
        contractId,
        files,
        groupId,
      );

      const newEntries = createResolvedMedia(
        payloads,
        links,
        contractId,
        userId,
      );

      const updatedItems = state.contracts.items.map((item) =>
        item.id === contractId
          ? {
              ...item,
              medias: [...(item.medias || []), ...newEntries],
              mediaLinks: null,
            }
          : item,
      );

      dispatch(setContracts(updatedItems));

      toast.success(
        i18n.t("common:slices.contracts.uploadSuccess", {
          count: files.length,
          defaultValue: `${files.length} files uploaded successfully.`,
        }),
      );
    } catch (err) {
      console.error("Contract Upload Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.uploadError", {
          defaultValue: "Upload failed",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const renameContractMedia = (
  contractId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();

      const currentContract = state.contracts.items.find(
        (contract) => contract.id === contractId,
      );

      const targetFile = currentContract?.medias?.find((m) => m.id === fileId);

      if (!targetFile) {
        toast.error(
          i18n.t("common:slices.contracts.fileNotFoundError", {
            defaultValue: "File not found to rename",
          }),
        );
        return;
      }

      const payload = buildRenamePayload(targetFile, contractId, newName);

      await contractApi.uploadMedia(contractId, payload);

      const updatedItems = state.contracts.items.map((item) =>
        item.id === contractId
          ? {
              ...item,
              medias: applyRename(item.medias || [], fileId, newName),
            }
          : item,
      );

      dispatch(setContracts(updatedItems));

      toast.success(
        i18n.t("common:slices.contracts.renameSuccess", {
          defaultValue: "File renamed successfully",
        }),
      );
    } catch (err) {
      console.error("Contract Media Rename Error:", err);
      toast.error(
        i18n.t("common:slices.contracts.renameError", {
          defaultValue: "Failed to rename file",
        }),
      );
    }
  };
};

export const removeContractMedia = (
  contractId: string,
  filesToRemove: Array<{ id: string }>,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!filesToRemove?.length) return;

      const state = getState();

      const currentContract = state.contracts.items.find(
        (contract) => contract.id === contractId,
      );

      if (!currentContract?.medias) return;

      const idsToRemove = filesToRemove.map((f) => f.id);

      const payload = buildDeletePayload(
        currentContract.medias,
        contractId,
        idsToRemove,
      );
      // Nothing to remove that this record still lists.
      if (payload.length === 0) return;

      await contractApi.uploadMedia(contractId, payload);

      const updatedItems = state.contracts.items.map((item) =>
        item.id === contractId
          ? {
              ...item,
              medias: applyDelete(item.medias || [], idsToRemove),
            }
          : item,
      );

      dispatch(setContracts(updatedItems));

      toast.success(
        i18n.t("common:slices.contracts.deleteImageSuccess", {
          defaultValue: "File removed",
        }),
      );
    } catch (err) {
      console.error("Contract Media Remove Error:", err);
      toast.error(
        i18n.t("common:slices.contracts.deleteImageError", {
          defaultValue: "Removal failed",
        }),
      );
    }
  };
};

export const addNewContract = (formData: Partial<Contract>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const res = await contractApi.createContract(formData);
      const newContract = { ...formData, id: res.id || res, isActive: true };
      const { items } = getState().contracts;

      dispatch(setContracts([newContract, ...items]));
      toast.success(
        i18n.t("common:slices.contracts.addSuccess", {
          defaultValue: "Contract created successfully!",
        }),
      );
    } catch (err) {
      console.error("Create Contract Error:", err);
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("common:slices.contracts.addError", {
            defaultValue: "Failed to create contract",
          }),
        ),
      );
    }
  };
};

export const editContract = (formData: UpdateContractPayload) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const response = await contractApi.updateContract(formData);
      const updatedContractFromServer = response?.data || response || formData;

      const { items } = getState().contracts;
      const updatedList = items.map((item: Contract) => {
        if (item.id === formData.id) {
          return {
            ...item,
            ...updatedContractFromServer,
          };
        }
        return item;
      });

      dispatch(setContracts(updatedList));

      toast.success(
        i18n.t("common:slices.contracts.editSuccess", {
          defaultValue: "Contract updated successfully!",
        }),
      );
    } catch (err) {
      console.error("Update Contract Error:", err);
      // The server names what it refused — a missing workflow role, a stale
      // id — and that reason is the only thing that tells someone what to fix.
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("common:slices.contracts.editError", {
            defaultValue: "Update failed",
          }),
        ),
      );
      throw err;
    }
  };
};

export const removeContract = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await contractApi.removeContract(id);
      const { items } = getState().contracts;

      dispatch(setContracts(items.filter((item: Contract) => item.id !== id)));

      toast.success(
        i18n.t("common:slices.contracts.removeSuccess", {
          defaultValue: "Contract removed successfully",
        }),
      );
    } catch (err) {
      console.error("Delete Contract Error:", err);
      // Rethrown, not swallowed. Callers await this and then announce
      // success: LegacyContractDetail showed "Legacy contract deleted" and
      // navigated to the list, and CardActions' confirm dialog showed its own
      // success toast — all for a delete the server had rejected.
      throw err;
    }
  };
};

export const uploadContractProfilePic = async (file: File): Promise<string> => {
  const fileId = uuidv4();
  const initRes = await contractApi.initUpload([fileId]);

  const uploadUrl = initRes.links[0].t;

  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type },
  });

  return fileId;
};

export const handleChangeContractStatus = (id: string, newStatus: number) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await contractApi.changeStatusContract({
        contractId: id,
        status: newStatus,
      });

      const { items } = getState().contracts;
      dispatch(
        setContracts(
          items.map((item: Contract) =>
            item.id === id ? { ...item, status: newStatus } : item,
          ),
        ),
      );

      toast.success(
        i18n.t("common:slices.contracts.statusChangeSuccess", {
          defaultValue: "Contract status updated successfully",
        }),
      );
    } catch (err) {
      console.error("Error changing contract status:", err);
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("common:slices.contracts.statusChangeError", {
            defaultValue: "Failed to update contract status",
          }),
        ),
      );
    }
  };
};

/**
 * Uploads a docx snapshot for a workflow action and returns the S3 key the
 * backend needs to attach it to the notification email. Deliberately calls
 * uploadMediaFiles directly rather than dispatching uploadContractMedias —
 * the latter doesn't expose the resulting S3 key, which the workflow
 * commands need.
 */
async function uploadWorkflowSnapshot(
  dispatch: AppDispatch,
  getState: () => RootState,
  contractId: string,
  docxFile: File,
): Promise<string> {
  const { items } = getState().contracts;
  const groupId = items.find(
    (item: Contract) => item.id === contractId,
  )?.groupId;

  const { payloads, links } = await uploadMediaFiles(
    contractApi,
    contractId,
    [docxFile],
    groupId,
  );
  const userId = (await getCurrentUser()).userId;
  const newEntries = createResolvedMedia(payloads, links, contractId, userId);

  dispatch(
    setContracts(
      items.map((item: Contract) =>
        item.id === contractId
          ? { ...item, medias: [...(item.medias || []), ...newEntries] }
          : item,
      ),
    ),
  );

  return payloads[0]?.payload?.s3Url;
}

/**
 * Removes a workflow docx snapshot that was uploaded right before a workflow
 * API call which then failed — otherwise an orphaned document (not actually
 * tied to any successful send) would be left behind in the Documents tab.
 * Silent by design: a cleanup failure shouldn't mask the original error.
 */
async function cleanupFailedWorkflowSnapshot(
  dispatch: AppDispatch,
  getState: () => RootState,
  contractId: string,
  mediaId: string,
) {
  try {
    const { items } = getState().contracts;
    const currentContract = items.find((item) => item.id === contractId);
    if (!currentContract?.medias?.length) return;

    const payload = buildDeletePayload(currentContract.medias, contractId, [
      mediaId,
    ]);
    // Nothing to remove that this record still lists.
    if (payload.length === 0) return;
    await contractApi.uploadMedia(contractId, payload);

    // The Delete above only soft-deletes the media — it never removes the
    // ContractDocumentVersionEntity row RecordNewVersionAsync already
    // created for this same upload (by design: a user deleting a document
    // that was genuinely sent should still keep its version-history entry).
    // This snapshot was never actually sent, so its version row is spurious
    // and would otherwise sit in Version History forever pointing at a
    // deleted file. Best-effort — a failure here shouldn't surface as the
    // send-failure error the caller is already handling.
    try {
      await contractApi.removeDocumentVersion(contractId, mediaId);
    } catch (versionCleanupErr) {
      console.error(
        "Failed to clean up orphaned document version after send failure:",
        versionCleanupErr,
      );
    }

    dispatch(
      setContracts(
        items.map((item: Contract) =>
          item.id === contractId
            ? { ...item, medias: applyDelete(item.medias || [], [mediaId]) }
            : item,
        ),
      ),
    );
  } catch (cleanupErr) {
    console.error(
      "Failed to clean up workflow snapshot after send failure:",
      cleanupErr,
    );
  }
}

function setContractStatusInState(
  dispatch: AppDispatch,
  getState: () => RootState,
  contractId: string,
  contracStatus: number,
) {
  const { items } = getState().contracts;
  dispatch(
    setContracts(
      items.map((item: Contract) =>
        item.id === contractId ? { ...item, contracStatus } : item,
      ),
    ),
  );
}

// Per-participant "wait for all" tracking means a Submit Decision/Complete
// Review call doesn't always move the contract to a new status — only once
// every assigned reviewer/approver has responded. So instead of guessing the
// resulting status locally, re-fetch this one contract and take whatever
// status/participants the backend actually landed on.
async function refetchContractAfterWorkflowAction(
  dispatch: AppDispatch,
  getState: () => RootState,
  contractId: string,
) {
  try {
    const userData = await getCurrentUser();
    const response = await contractApi.searchContracts({
      pageSize: 1,
      pageIndex: 0,
      groupIds: [`${userData.companyId}`],
      ids: [contractId],
      currentProfileId: userData.currentLoggedInUserId || "",
      myMode: false,
      // Only status, participants and versions are read below — not the
      // document body (the server default), nor facets.
      includeDocument: false,
      includeFacets: false,
    });
    const fetched = (response.data || [])[0];
    if (!fetched) return;

    const { items } = getState().contracts;
    dispatch(
      setContracts(
        items.map((item: Contract) =>
          item.id === contractId
            ? {
                ...item,
                contracStatus: fetched.contracStatus,
                participants: fetched.participants,
                documentVersions: fetched.documentVersions,
              }
            : item,
        ),
      ),
    );
  } catch (err) {
    console.error("Refetch contract after workflow action failed:", err);
  }
}

export const sendContractForApproval = (
  contract: Contract,
  docxFile: File,
  params: { subject: string; body: string },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    let docxS3Key: string | undefined;
    try {
      docxS3Key = await uploadWorkflowSnapshot(
        dispatch,
        getState,
        contract.id,
        docxFile,
      );

      await contractWorkflowApi.sendForApproval(
        contract.id,
        docxS3Key,
        docxFile.name,
        params.subject,
        params.body,
      );

      setContractStatusInState(
        dispatch,
        getState,
        contract.id,
        ContractWorkflowStatus.SentForApproval,
      );

      toast.success(
        i18n.t("common:slices.contracts.sendForApprovalSuccess", {
          defaultValue: "Contract sent for approval.",
        }),
      );
    } catch (err) {
      if (docxS3Key) {
        await cleanupFailedWorkflowSnapshot(
          dispatch,
          getState,
          contract.id,
          docxS3Key,
        );
      }
      console.error("Send For Approval Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.sendForApprovalError", {
          defaultValue: "Failed to send contract for approval.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const sendContractToReviewer = (
  contract: Contract,
  docxFile: File,
  params: { subject: string; body: string },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    let docxS3Key: string | undefined;
    try {
      docxS3Key = await uploadWorkflowSnapshot(
        dispatch,
        getState,
        contract.id,
        docxFile,
      );

      await contractWorkflowApi.sendToReviewer(
        contract.id,
        docxS3Key,
        docxFile.name,
        params.subject,
        params.body,
      );

      setContractStatusInState(
        dispatch,
        getState,
        contract.id,
        ContractWorkflowStatus.SentToReviewer,
      );

      toast.success(
        i18n.t("common:slices.contracts.sendToReviewerSuccess", {
          defaultValue: "Contract sent to reviewer.",
        }),
      );
    } catch (err) {
      if (docxS3Key) {
        await cleanupFailedWorkflowSnapshot(
          dispatch,
          getState,
          contract.id,
          docxS3Key,
        );
      }
      console.error("Send To Reviewer Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.sendToReviewerError", {
          defaultValue: "Failed to send contract to reviewer.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const sendContractForExternalReview = (
  contract: Contract,
  docxFile: File,
  params: {
    externalEmails: string[];
    ccEmails: string[];
    subject: string;
    body: string;
    attachments?: { s3Key: string; fileName: string }[];
  },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    let docxS3Key: string | undefined;
    try {
      docxS3Key = await uploadWorkflowSnapshot(
        dispatch,
        getState,
        contract.id,
        docxFile,
      );

      await contractWorkflowApi.sendForExternalReview(
        contract.id,
        docxS3Key,
        docxFile.name,
        params.externalEmails,
        params.ccEmails,
        params.subject,
        params.body,
        params.attachments ?? [],
      );

      setContractStatusInState(
        dispatch,
        getState,
        contract.id,
        ContractWorkflowStatus.SentForExternalReview,
      );

      toast.success(
        i18n.t("common:slices.contracts.sendForExternalReviewSuccess", {
          emails: params.externalEmails.join(", "),
          defaultValue: `Sent for external review to ${params.externalEmails.join(", ")}`,
        }),
      );
    } catch (err) {
      if (docxS3Key) {
        await cleanupFailedWorkflowSnapshot(
          dispatch,
          getState,
          contract.id,
          docxS3Key,
        );
      }
      console.error("Send For External Review Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.sendForExternalReviewError", {
          defaultValue: "Failed to send contract for external review.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const sendContractToEditor = (
  contract: Contract,
  docxFile: File,
  params: { subject: string; body: string },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    let docxS3Key: string | undefined;
    try {
      docxS3Key = await uploadWorkflowSnapshot(
        dispatch,
        getState,
        contract.id,
        docxFile,
      );

      await contractWorkflowApi.sendToEditor(
        contract.id,
        docxS3Key,
        docxFile.name,
        params.subject,
        params.body,
      );

      setContractStatusInState(
        dispatch,
        getState,
        contract.id,
        ContractWorkflowStatus.UnderReview,
      );

      toast.success(
        i18n.t("common:slices.contracts.sendToEditorSuccess", {
          defaultValue: "Contract sent to editor.",
        }),
      );
    } catch (err) {
      if (docxS3Key) {
        await cleanupFailedWorkflowSnapshot(
          dispatch,
          getState,
          contract.id,
          docxS3Key,
        );
      }
      console.error("Send To Editor Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.sendToEditorError", {
          defaultValue: "Failed to send contract to editor.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const sendContractToRepository = (
  contract: Contract,
  docxFile: File,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    let docxS3Key: string | undefined;
    try {
      docxS3Key = await uploadWorkflowSnapshot(
        dispatch,
        getState,
        contract.id,
        docxFile,
      );

      await contractWorkflowApi.sendToRepository(
        contract.id,
        docxS3Key,
        docxFile.name,
      );

      setContractStatusInState(
        dispatch,
        getState,
        contract.id,
        ContractWorkflowStatus.SentToRepository,
      );

      toast.success(
        i18n.t("common:slices.contracts.sendToRepositorySuccess", {
          defaultValue: "Contract sent to the repository.",
        }),
      );
    } catch (err) {
      if (docxS3Key) {
        await cleanupFailedWorkflowSnapshot(
          dispatch,
          getState,
          contract.id,
          docxS3Key,
        );
      }
      console.error("Send To Repository Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.sendToRepositoryError", {
          defaultValue: "Failed to send contract to the repository.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const sendContractForSignature = (
  contract: Contract,
  recipients: Array<{ name: string; email: string }>,
  signatureProvider?: string,
  nativeSigning?: import("../../api/nativeSigningApi").NativeSigningSendOptions,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      // The document was already finalized in the "Send to Repository" step
      // — reuse that snapshot rather than generating a new one, since the
      // contract is locked (no content changes possible) by this point.
      const docxMedia = [...(contract.medias || [])]
        .reverse()
        .find((m) =>
          (m.fileName || m.name || "").toLowerCase().endsWith(".docx"),
        );

      if (!docxMedia) {
        throw new Error(
          i18n.t("slices.contracts.noFinalDocument", { ns: "common" }),
        );
      }

      await contractWorkflowApi.sendForSignature(
        contract.id,
        docxMedia.s3Url,
        docxMedia.fileName || docxMedia.name || "contract.docx",
        recipients,
        signatureProvider,
        nativeSigning,
      );

      setContractStatusInState(
        dispatch,
        getState,
        contract.id,
        ContractWorkflowStatus.PendingSignature,
      );

      toast.success(
        i18n.t("common:slices.contracts.sendForSignatureSuccess", {
          defaultValue: "Contract sent for signature.",
        }),
      );
    } catch (err) {
      console.error("Send For Signature Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.sendForSignatureError", {
          defaultValue: "Failed to send contract for signature.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const submitApprovalDecision = (
  contractId: string,
  decisionType: number,
  comment: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await contractWorkflowApi.submitApprovalDecision(
        contractId,
        decisionType,
        comment,
      );
      await refetchContractAfterWorkflowAction(dispatch, getState, contractId);
      toast.success(
        i18n.t("common:slices.contracts.submitDecisionSuccess", {
          defaultValue: "Decision submitted.",
        }),
      );
    } catch (err) {
      console.error("Submit Approval Decision Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.submitDecisionError", {
          defaultValue: "Failed to submit decision.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const completeContractReview = (contractId: string, comment: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await contractWorkflowApi.completeReview(contractId, comment);
      await refetchContractAfterWorkflowAction(dispatch, getState, contractId);
      toast.success(
        i18n.t("common:slices.contracts.completeReviewSuccess", {
          defaultValue: "Review completed.",
        }),
      );
    } catch (err) {
      console.error("Complete Review Error:", err);
      const message = getApiErrorMessage(
        err,
        i18n.t("common:slices.contracts.completeReviewError", {
          defaultValue: "Failed to complete review.",
        }),
      );
      toast.error(message);
      throw new Error(message);
    }
  };
};

export const renameContractFile = (
  contractId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();

      const currentContract = state.contracts.items.find(
        (c: Contract) => c.id === contractId,
      );

      const targetFile = currentContract?.medias?.find((m) => m.id === fileId);

      if (!targetFile) {
        toast.error(i18n.t("slices.shared.fileNotFound", { ns: "common" }));
        return;
      }

      const payload = buildRenamePayload(targetFile, contractId, newName);

      await contractApi.uploadMedia(contractId, payload);

      const updatedItems = state.contracts.items.map((item: Contract) =>
        item.id === contractId
          ? {
              ...item,
              medias: applyRename(item.medias || [], fileId, newName),
            }
          : item,
      );

      dispatch(setContracts(updatedItems));

      toast.success(
        i18n.t("common:slices.contracts.renameSuccess", {
          defaultValue: "File renamed successfully",
        }),
      );
    } catch (err) {
      console.error("Contract Rename Error:", err);

      toast.error(
        i18n.t("common:slices.contracts.renameError", {
          defaultValue: "Failed to rename file",
        }),
      );
    }
  };
};

export interface ContractTypeItem {
  id: string;
  contractName: string;
}

export interface ContractCategoryItem {
  id: string;
  contractCategory: string;
}

export interface ContractTitleItem {
  id: string;
  contractTitle: string;
}

interface ContractsState {
  items: Contract[];
  contractTypes: ContractTypeItem[];
  contractCategories: ContractCategoryItem[];
  contractTitles: ContractTitleItem[];
  isLoading: boolean;
  pageIndex: number;
  pageIndexByType: number;
  pageIndexByCategory: number;
  pageIndexByTitle: number;
  hasMore: boolean;
  hasMoreByType: boolean;
  hasMoreByCategory: boolean;
  hasMoreByTitle: boolean;
  totalContracts: number;
  totalContractTypes: number;
  totalContractCategories: number;
  totalContractTitles: number;
}

const initialState: ContractsState = {
  items: [],
  contractTypes: [],
  contractCategories: [],
  contractTitles: [],
  isLoading: false,
  pageIndex: 0,
  pageIndexByType: 0,
  pageIndexByCategory: 0,
  pageIndexByTitle: 0,
  hasMore: true,
  hasMoreByType: true,
  hasMoreByCategory: true,
  hasMoreByTitle: true,
  totalContracts: 0,
  totalContractTypes: 0,
  totalContractCategories: 0,
  totalContractTitles: 0,
};

const contractsSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    setContracts: (state, action) => {
      state.items = action.payload;
      state.isLoading = false;
    },
    appendContracts: (state, action) => {
      // Defensive dedup by id — the isLoading guard in fetchContracts is
      // the real fix for the race that could double-append the same page,
      // but this is cheap insurance against any other path landing here
      // with an id already in the list.
      const existingIds = new Set(state.items.map((c: Contract) => c.id));
      const newItems = action.payload.filter(
        (c: Contract) => !existingIds.has(c.id),
      );
      state.items = [...state.items, ...newItems];
      state.isLoading = false;
    },
    setContractTypes: (state, action) => {
      state.contractTypes = action.payload;
      state.isLoading = false;
    },
    appendContractTypes: (state, action) => {
      state.contractTypes = [...state.contractTypes, ...action.payload];
      state.isLoading = false;
    },
    setContractCategories: (state, action) => {
      state.contractCategories = action.payload;
      state.isLoading = false;
    },
    appendContractCategories: (state, action) => {
      state.contractCategories = [
        ...state.contractCategories,
        ...action.payload,
      ];
      state.isLoading = false;
    },
    setContractTitles: (state, action) => {
      state.contractTitles = action.payload;
      state.isLoading = false;
    },
    appendContractTitles: (state, action) => {
      state.contractTitles = [...state.contractTitles, ...action.payload];
      state.isLoading = false;
    },
    setPageIndex: (state, action) => {
      state.pageIndex = action.payload;
    },
    setPageIndexByType: (state, action) => {
      state.pageIndexByType = action.payload;
    },
    setPageIndexByCategory: (state, action) => {
      state.pageIndexByCategory = action.payload;
    },
    setPageIndexByTitle: (state, action) => {
      state.pageIndexByTitle = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setHasMoreByType: (state, action) => {
      state.hasMoreByType = action.payload;
    },
    setHasMoreByCategory: (state, action) => {
      state.hasMoreByCategory = action.payload;
    },
    setHasMoreByTitle: (state, action) => {
      state.hasMoreByTitle = action.payload;
    },
    setTotalContracts: (state, action) => {
      state.totalContracts = action.payload;
    },
    setTotalContractsByType: (state, action) => {
      state.totalContractTypes = action.payload;
    },
    setTotalContractCategories: (state, action) => {
      state.totalContractCategories = action.payload;
    },
    setTotalContractTitles: (state, action) => {
      state.totalContractTitles = action.payload;
    },
    setContractLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    resetContractList: (state) => {
      state.items = [];
      state.pageIndex = 0;
      state.hasMore = true;
    },
    resetContractTypeList: (state) => {
      state.contractTypes = [];
      state.pageIndexByType = 0;
      state.hasMoreByType = true;
    },
    resetContractCategoryList: (state) => {
      state.contractCategories = [];
      state.pageIndexByCategory = 0;
      state.hasMoreByCategory = true;
    },
    resetContractTitleList: (state) => {
      state.contractTitles = [];
      state.pageIndexByTitle = 0;
      state.hasMoreByTitle = true;
    },
  },
});

export const {
  setContracts,
  appendContracts,
  setContractTypes,
  appendContractTypes,
  setContractCategories,
  appendContractCategories,
  setContractTitles,
  appendContractTitles,
  setContractLoading,
  resetContractList,
  resetContractTypeList,
  resetContractCategoryList,
  resetContractTitleList,
  setHasMore,
  setHasMoreByType,
  setHasMoreByCategory,
  setHasMoreByTitle,
  setPageIndex,
  setPageIndexByType,
  setPageIndexByCategory,
  setPageIndexByTitle,
  setTotalContracts,
  setTotalContractsByType,
  setTotalContractCategories,
  setTotalContractTitles,
} = contractsSlice.actions;

export default contractsSlice.reducer;
